import React, { useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Col, Container, Row, Form, FormGroup, Input, Label } from "reactstrap";
import { Btn, H2, H6, P, Image } from "../AbstractElements";
import { dynamicImage } from "../Utils";
import {
  CreateYourPassword,
  Done,
  EnterOtp,
  Href,
  NewPassword,
  ResetYourPassword,
  RetypePassword,
  Send,
  SignIn,
  Resend,
} from "../Utils/Constants";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { baseUrl } from "../Utils/FileUpload";

export default function ForgetPassword() {
  const [isPasswordVisible, setPasswordVisible] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [loading, setLoading] = useState(false);
  const [otpVerifying, setOtpVerifying] = useState(false);
  const [resettingPassword, setResettingPassword] = useState(false);
  const navigate = useNavigate();
  const [timer, setTimer] = useState<number>(60);
  const [otpStartTime, setOtpStartTime] = useState<number | null>(null);

  const [formData, setFormData] = useState({
    email: "",
    otp: ["", "", "", "", "", ""],
    newPassword: "",
    retypePassword: "",
  });

  const otpInputRefs = useRef<HTMLInputElement[]>([]);

  const toggle = () => setPasswordVisible(!isPasswordVisible);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    index?: number
  ) => {
    const { id, value } = e.target;

    if (index !== undefined) {
      if (/^[0-9]?$/.test(value)) {
        const newOtp = [...formData.otp];
        newOtp[index] = value;
        setFormData((prev) => ({ ...prev, otp: newOtp }));

        if (value && index < 5) {
          otpInputRefs.current[index + 1]?.focus();
        }
      }
    } else {
      setFormData((prevData) => ({
        ...prevData,
        [id]: value,
      }));
    }
  };

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    index: number
  ) => {
    if (e.key === "Backspace") {
      if (formData.otp[index] === "" && index > 0) {
        otpInputRefs.current[index - 1]?.focus();
      }
    }
  };

  const sendOtp = async () => {
    try {
      setLoading(true);
      const res = await axios.post(`${baseUrl}send-otp`, {
        email: formData.email,
      });
      console.log(res,"res")
      toast.success(res.data.message);
      setOtpSent(true);
      setOtpVerified(false); // reset verification state
      setFormData((prev) => ({ ...prev, otp: ["", "", "", "", "", ""] }));

      // Start 60 second timer
      setOtpStartTime(Date.now());
      setTimer(60);
      const interval = setInterval(() => {
        setTimer((prev) => {
          if (prev <= 1) {
            clearInterval(interval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      setTimeout(() => {
        otpInputRefs.current[0]?.focus();
      }, 100);
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Failed to send OTP");
    } finally {
      setLoading(false);
    }
  };

  const verifyOtp = async () => {
    if (!otpStartTime || Date.now() - otpStartTime > 60000) {
      toast.error("OTP expired. Try again.");
      return;
    }

    try {
      setOtpVerifying(true);
      const otp = formData.otp.join("");
      const res = await axios.post(`${baseUrl}verify-otp`, {
        email: formData.email,
        otp,
      });
      toast.success(res.data.message);
      setOtpVerified(true);
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Invalid OTP");
      setOtpVerified(false);
    } finally {
      setOtpVerifying(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    try {
      setResettingPassword(true);
      const otp = formData.otp.join("");
      const res = await axios.post(`${baseUrl}reset-password`, {
        email: formData.email,
        otp,
        password: formData.newPassword,
        password_confirmation: formData.retypePassword,
      });
      toast.success(res.data.message, {
        onClose: () => navigate("/login"),
        autoClose: 2000,
        pauseOnHover: true,
      });
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Failed to reset password");
    } finally {
      setResettingPassword(false);
    }
  };

  return (
    <div className="page-wrapper">
      <Container fluid className="p-0">
        <Row>
          <Col xs={12}>
            <div className="login-card login-dark">
              <div>
                <div className="text-center mb-4">
                  <Link className="logo" to={`/dashboard/default`}>
                    <Image
                      className="img-fluid for-light m-auto"
                      src={dynamicImage(`logo/Logo.png`)}
                      alt="loginpage"
                    />
                    <Image
                      className="img-fluid for-dark"
                      src={dynamicImage(`logo/Logo.png`)}
                      alt="loginpage"
                    />
                  </Link>
                </div>
                <div className="login-main">
                  <Form className="theme-form" onSubmit={handleSubmit}>
                    <H2>{ResetYourPassword}</H2>

                    <FormGroup>
                      <Label className="pt-0">Enter your email</Label>
                      <Input
                        type="email"
                        id="email"
                        value={formData.email}
                        placeholder="Email"
                        onChange={handleInputChange}
                        required
                        disabled={otpSent || otpVerified}
                      />
                      <div className="text-end mt-2">
                        <Btn
                          color="primary"
                          block
                          className="m-t-10"
                          onClick={sendOtp}
                          type="button"
                          disabled={otpVerified}
                        >
                          {loading ? "Sending..." : Send}
                        </Btn>
                      </div>
                    </FormGroup>

                    {otpSent && !otpVerified && (
                      <div className="mt-4 mb-4 text-center">
                        <span className="reset-password-link">
                          {"Didn't receive OTP? "}
                          <a
                            className="btn-link text-danger"
                            href={Href}
                            onClick={(e) => {
                              e.preventDefault();
                              sendOtp();
                            }}
                          >
                            {loading ? "Sending..." : Resend}
                          </a>
                        </span>

                        {timer > 0 && (
                          <div className="text-danger mt-2">
                            OTP expires in {timer}s
                          </div>
                        )}
                        {timer === 0 && (
                          <div className="text-warning mt-2">
                            OTP expired. Please resend.
                          </div>
                        )}
                      </div>
                    )}

                    <FormGroup>
                      <Label className="pt-0">{EnterOtp}</Label>
                      <Row>
                        {[0, 1, 2, 3, 4, 5].map((i) => (
                          <Col key={i} xs={2}>
                            <Input
                              className="text-center opt-text"
                              type="text"
                              maxLength={1}
                              value={formData.otp[i]}
                              onChange={(e) => handleInputChange(e, i)}
                              onKeyDown={(e) => handleKeyDown(e, i)}
                              disabled={!otpSent || otpVerified}
                              innerRef={(el) =>
                                (otpInputRefs.current[i] =
                                  el as HTMLInputElement)
                              }
                            />
                          </Col>
                        ))}
                      </Row>
                      <div className="text-end mt-2">
                        <Btn
                          color="primary"
                          className="m-t-10 w-100"
                          onClick={verifyOtp}
                          type="button"
                          block
                          disabled={!otpSent || otpVerifying  || otpVerified}
                        >
                          {otpVerifying ? "Verifying..." : "Verify OTP"}
                        </Btn>
                      </div>
                    </FormGroup>

                    <H6 className="mt-4 f-w-700">{CreateYourPassword}</H6>

                    <FormGroup>
                      <Label>{NewPassword}</Label>
                      <div className="form-input position-relative">
                        <Input
                          type={isPasswordVisible ? "text" : "password"}
                          id="newPassword"
                          value={formData.newPassword}
                          onChange={handleInputChange}
                          required
                          placeholder="*********"
                          disabled={!otpVerified}
                        />
                        <div className="show-hide" onClick={toggle}>
                          <span
                            className={!isPasswordVisible ? "show" : ""}
                          ></span>
                        </div>
                      </div>
                    </FormGroup>

                    <FormGroup>
                      <Label>{RetypePassword}</Label>
                      <div className="form-input position-relative">
                        <Input
                          type={isPasswordVisible ? "text" : "password"}
                          id="retypePassword"
                          value={formData.retypePassword}
                          onChange={handleInputChange}
                          required
                          placeholder="*********"
                          disabled={!otpVerified}
                        />
                        <div className="show-hide" onClick={toggle}></div>
                      </div>
                    </FormGroup>

                    <Btn
                      type="submit"
                      color="primary"
                      className="w-100 mt-3"
                      block
                      disabled={resettingPassword || !otpVerified}
                    >
                      {resettingPassword ? "Resetting..." : Done}
                    </Btn>

                    <P className="mt-4 mb-0 text-center">
                      Already have an account?
                      <Link className="ms-2" to={`/login`}>
                        {SignIn}
                      </Link>
                    </P>
                  </Form>
                </div>
              </div>
            </div>
          </Col>
        </Row>
      </Container>
      <ToastContainer position="top-right" autoClose={2000} />
    </div>
  );
}

// import React, { useEffect, useRef, useState } from "react";
// import { Link, useNavigate } from "react-router-dom";
// import { Col, Container, Row, Form, FormGroup, Input, Label } from "reactstrap";
// import { Btn, H2, H6, P, Image } from "../AbstractElements";
// import { dynamicImage } from "../Utils";
// import {
//   CreateYourPassword,
//   Done,
//   EnterOtp,
//   Href,
//   NewPassword,
//   ResetYourPassword,
//   RetypePassword,
//   Send,
//   SignIn,
//   Resend,
// } from "../Utils/Constants";
// import axios from "axios";
// import { toast, ToastContainer } from "react-toastify";
// import "react-toastify/dist/ReactToastify.css";
// import { baseUrl } from "../Utils/FileUpload";
// import useCustomToasts from "../hooks/useCustomToast";

// export default function ForgetPassword() {
//   const [isPasswordVisible, setPasswordVisible] = useState(false);
//   const [otpSent, setOtpSent] = useState(false);
//   const [otpVerified, setOtpVerified] = useState(false);
//   const [loading, setLoading] = useState(false);
//   const [otpVerifying, setOtpVerifying] = useState(false);
//   const [resettingPassword, setResettingPassword] = useState(false);
//   const navigate = useNavigate();
//   const { SuccessToast, ErrorToast } = useCustomToasts(navigate);
//   const [otpTimer, setOtpTimer] = useState(60); //1min
//   const [timerActive, setTimerActive] = useState(false);
//   const [formData, setFormData] = useState({
//     email: "",
//     otp: ["", "", "", "", "", ""],
//     newPassword: "",
//     retypePassword: "",
//   });

//   const otpInputRefs = useRef<HTMLInputElement[]>([]);

//   const toggle = () => setPasswordVisible(!isPasswordVisible);

//   const handleInputChange = (
//     e: React.ChangeEvent<HTMLInputElement>,
//     index?: number
//   ) => {
//     const { id, value } = e.target;

//     if (index !== undefined) {
//       if (/^[0-9]?$/.test(value)) {
//         const newOtp = [...formData.otp];
//         newOtp[index] = value;
//         setFormData((prev) => ({ ...prev, otp: newOtp }));

//         if (value && index < 5) {
//           otpInputRefs.current[index + 1]?.focus();
//         }
//       }
//     } else {
//       setFormData((prevData) => ({
//         ...prevData,
//         [id]: value,
//       }));
//     }
//   };

//   const handleKeyDown = (
//     e: React.KeyboardEvent<HTMLInputElement>,
//     index: number
//   ) => {
//     if (e.key === "Backspace") {
//       if (formData.otp[index] === "" && index > 0) {
//         otpInputRefs.current[index - 1]?.focus();
//       }
//     }
//   };

//   useEffect(() => {
//     let interval: NodeJS.Timeout;

//     // Only run timer if OTP is not yet verified
//     if (timerActive && otpTimer > 0 && !otpVerified) {
//       interval = setInterval(() => {
//         setOtpTimer((prev) => prev - 1);
//       }, 1000);
//     } else if (otpTimer === 0 && timerActive && !otpVerified) {
//       setTimerActive(false);
//       // Reset OTP fields and disable them
//       setFormData((prev) => ({
//         ...prev,
//         otp: ["", "", "", "", "", ""],
//       }));
//       setOtpSent(false);
//       if (!toast.isActive("otp-expired")) {
//         toast.error("OTP expired. Please request a new one.", {
//           toastId: "otp-expired",
//         });
//       }
//     }

//     return () => {
//       if (interval) clearInterval(interval);
//     };
//   }, [timerActive, otpTimer, otpVerified]); // Add otpVerified to dependencies

//   const formatTime = (seconds: number) => {
//     const m = Math.floor(seconds / 60)
//       .toString()
//       .padStart(2, "0");
//     const s = (seconds % 60).toString().padStart(2, "0");
//     return `${m}:${s}`;
//   };

//   const sendOtp = async () => {
//     try {
//       setLoading(true);
//       // Clear previous OTP
//       setFormData((prev) => ({
//         ...prev,
//         otp: ["", "", "", "", "", ""],
//       }));

//       const res = await axios.post(`${baseUrl}send-otp`, {
//         email: formData.email,
//       });

//       // Use the toast directly from react-toastify
//       toast.success(res.data.message, {
//         toastId: "otp-sent", // Add a unique ID to prevent duplicates
//         autoClose: 3000, // Close after 3 seconds
//       });

//       setOtpSent(true);
//       setOtpVerified(false);

//       if (res.data.expires_in) {
//         setOtpTimer(res.data.expires_in);
//       } else {
//         setOtpTimer(60); // fallback
//       }

//       setTimerActive(true);
//       setTimeout(() => {
//         otpInputRefs.current[0]?.focus();
//       }, 100);
//     } catch (error: any) {
//       toast.error(error.response?.data?.message || "Failed to send OTP", {
//         toastId: "otp-error", // Add a unique ID to prevent duplicates
//         autoClose: 3000, // Close after 3 seconds
//       });
//     } finally {
//       setLoading(false);
//     }
//   };

//   const verifyOtp = async () => {
//     try {
//       setOtpVerifying(true);
//       const otp = formData.otp.join("");
//       const res = await axios.post(`${baseUrl}verify-otp`, {
//         email: formData.email,
//         otp,
//       });
//       toast.success(res.data.message, { toastId: "otp-verified" });
//       setOtpVerified(true);
//       setTimerActive(false); // Explicitly stop the timer
//     } catch (error: any) {
//       toast.error(error.response?.data?.message || "Invalid OTP", {
//         toastId: "otp-error",
//       });
//       setOtpVerified(false);
//     } finally {
//       setOtpVerifying(false);
//     }
//   };

//   const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
//     e.preventDefault();
//     try {
//       setResettingPassword(true);
//       const otp = formData.otp.join("");
//       const res = await axios.post(`${baseUrl}reset-password`, {
//         email: formData.email,
//         otp,
//         password: formData.newPassword,
//         password_confirmation: formData.retypePassword,
//       });
//       SuccessToast(res.data.message, "/login");
//     } catch (error: any) {
//       toast.error(error.response?.data?.message || "Failed to reset password");
//     } finally {
//       setResettingPassword(false);
//     }
//   };

//   return (
//     <div className="page-wrapper">
//       <Container fluid className="p-0">
//         <Row>
//           <Col xs={12}>
//             <div className="login-card login-dark">
//               <div>
//                 <div className="text-center mb-4">
//                   <Link className="logo" to={`/dashboard/default`}>
//                     <Image
//                       className="img-fluid for-light m-auto"
//                       src={dynamicImage(`logo/Logo.png`)}
//                       alt="loginpage"
//                     />
//                     <Image
//                       className="img-fluid for-dark"
//                       src={dynamicImage(`logo/Logo.png`)}
//                       alt="loginpage"
//                     />
//                   </Link>
//                 </div>
//                 <div className="login-main">
//                   <Form className="theme-form" onSubmit={handleSubmit}>
//                     <H2>{ResetYourPassword}</H2>

//                     <FormGroup>
//                       <Label className="pt-0">Enter your email</Label>
//                       <Input
//                         type="email"
//                         id="email"
//                         value={formData.email}
//                         placeholder="Email"
//                         onChange={handleInputChange}
//                         required
//                         disabled={otpSent}
//                       />
//                       <div className="text-end mt-2">
//                         <Btn
//                           color="primary"
//                           block
//                           className="m-t-10"
//                           onClick={sendOtp}
//                           type="button"
//                         >
//                           {loading ? "Sending..." : Send}
//                         </Btn>
//                       </div>

//                       {otpSent && (
//                         <div className="text-center text-muted mt-2">
//                           Time remaining:{" "}
//                           <strong>{formatTime(otpTimer)}</strong>
//                         </div>
//                       )}
//                     </FormGroup>

//                     {otpSent && (
//                       <div className="mt-4 mb-4">
//                         <span className="reset-password-link">
//                           {"Didn't receive OTP? "}
//                           <a
//                             className="btn-link text-danger"
//                             href={Href}
//                             onClick={(e) => {
//                               e.preventDefault();
//                               sendOtp();
//                             }}
//                           >
//                             {loading ? "Sending..." : Resend}
//                           </a>
//                         </span>
//                       </div>
//                     )}

//                     <FormGroup>
//                       <Label className="pt-0">{EnterOtp}</Label>
//                       <Row>
//                         {[0, 1, 2, 3, 4, 5].map((i) => (
//                           <Col key={i} xs={2}>
//                             <Input
//                               className="text-center opt-text"
//                               type="text"
//                               maxLength={1}
//                               value={formData.otp[i]}
//                               onChange={(e) => handleInputChange(e, i)}
//                               onKeyDown={(e) => handleKeyDown(e, i)}
//                               disabled={!otpSent || !timerActive}
//                               innerRef={(el) =>
//                                 (otpInputRefs.current[i] =
//                                   el as HTMLInputElement)
//                               }
//                             />
//                           </Col>
//                         ))}
//                       </Row>
//                       <div className="text-end mt-2">
//                         <Btn
//                           color="primary"
//                           className="m-t-10 w-100"
//                           onClick={verifyOtp}
//                           type="button"
//                           block
//                           disabled={!otpSent || otpVerifying || !timerActive}
//                         >
//                           {otpVerifying ? "Verifying..." : "Verify OTP"}
//                         </Btn>
//                       </div>
//                     </FormGroup>

//                     <H6 className="mt-4 f-w-700">{CreateYourPassword}</H6>

//                     <FormGroup>
//                       <Label>{NewPassword}</Label>
//                       <div className="form-input position-relative">
//                         <Input
//                           type={isPasswordVisible ? "text" : "password"}
//                           id="newPassword"
//                           value={formData.newPassword}
//                           onChange={handleInputChange}
//                           required
//                           placeholder="*********"
//                           disabled={!otpVerified}
//                         />
//                         <div className="show-hide" onClick={toggle}>
//                           <span
//                             className={!isPasswordVisible ? "show" : ""}
//                           ></span>
//                         </div>
//                       </div>
//                     </FormGroup>

//                     <FormGroup>
//                       <Label>{RetypePassword}</Label>
//                       <div className="form-input position-relative">
//                         <Input
//                           type={isPasswordVisible ? "text" : "password"}
//                           id="retypePassword"
//                           value={formData.retypePassword}
//                           onChange={handleInputChange}
//                           required
//                           placeholder="*********"
//                           disabled={!otpVerified}
//                         />
//                         <div className="show-hide" onClick={toggle}></div>
//                       </div>
//                     </FormGroup>

//                     <Btn
//                       type="submit"
//                       color="primary"
//                       className="w-100 mt-3"
//                       block
//                       disabled={resettingPassword || !otpVerified}
//                     >
//                       {resettingPassword ? "Resetting..." : Done}
//                     </Btn>

//                     <P className="mt-4 mb-0 text-center">
//                       Already have an account?
//                       <Link className="ms-2" to={`/login`}>
//                         {SignIn}
//                       </Link>
//                     </P>
//                   </Form>
//                 </div>
//               </div>
//             </div>
//           </Col>
//         </Row>
//       </Container>
//       <ToastContainer position="top-right" autoClose={3000} />
//     </div>
//   );
// }

// import React, { useRef, useState } from "react";
// import { Link, useNavigate } from "react-router-dom";
// import { Col, Container, Row, Form, FormGroup, Input, Label } from "reactstrap";
// import { Btn, H2, H6, P, Image } from "../AbstractElements";
// import { dynamicImage } from "../Utils";
// import {
//   CreateYourPassword,
//   Done,
//   EnterOtp,
//   Href,
//   NewPassword,
//   ResetYourPassword,
//   RetypePassword,
//   Send,
//   SignIn,
//   Resend,
// } from "../Utils/Constants";
// import axios from "axios";
// import { toast, ToastContainer } from "react-toastify";
// import "react-toastify/dist/ReactToastify.css";
// import { baseUrl } from "../Utils/FileUpload";
// import useCustomToasts from "../hooks/useCustomToast";

// export default function ForgetPassword() {
//   const [isPasswordVisible, setPasswordVisible] = useState(false);
//   const [otpSent, setOtpSent] = useState(false);
//   const [otpVerified, setOtpVerified] = useState(false);
//   const [loading, setLoading] = useState(false);
//   const [otpVerifying, setOtpVerifying] = useState(false);
//   const [resettingPassword, setResettingPassword] = useState(false);
//   const navigate = useNavigate();
//   const { SuccessToast, ErrorToast } = useCustomToasts(navigate);

//   const [formData, setFormData] = useState({
//     email: "",
//     otp: ["", "", "", "", "", ""],
//     newPassword: "",
//     retypePassword: "",
//   });

//   const otpInputRefs = useRef<HTMLInputElement[]>([]);

//   const toggle = () => setPasswordVisible(!isPasswordVisible);

//   const handleInputChange = (
//     e: React.ChangeEvent<HTMLInputElement>,
//     index?: number
//   ) => {
//     const { id, value } = e.target;

//     if (index !== undefined) {
//       if (/^[0-9]?$/.test(value)) {
//         const newOtp = [...formData.otp];
//         newOtp[index] = value;
//         setFormData((prev) => ({ ...prev, otp: newOtp }));

//         if (value && index < 5) {
//           otpInputRefs.current[index + 1]?.focus();
//         }
//       }
//     } else {
//       setFormData((prevData) => ({
//         ...prevData,
//         [id]: value,
//       }));
//     }
//   };

//   const handleKeyDown = (
//     e: React.KeyboardEvent<HTMLInputElement>,
//     index: number
//   ) => {
//     if (e.key === "Backspace") {
//       if (formData.otp[index] === "" && index > 0) {
//         otpInputRefs.current[index - 1]?.focus();
//       }
//     }
//   };

//   const sendOtp = async () => {
//     try {
//       setLoading(true);
//       const res = await axios.post(`${baseUrl}send-otp`, {
//         email: formData.email,
//       });
//       toast.success(res.data.message);
//       setOtpSent(true);
//       setTimeout(() => {
//         otpInputRefs.current[0]?.focus();
//       }, 100);
//     } catch (error: any) {
//       toast.error(error.response?.data?.message || "Failed to send OTP");
//     } finally {
//       setLoading(false);
//     }
//   };

//   const verifyOtp = async () => {
//     try {
//       setOtpVerifying(true);
//       const otp = formData.otp.join("");
//       const res = await axios.post(`${baseUrl}verify-otp`, {
//         email: formData.email,
//         otp,
//       });
//       toast.success(res.data.message);
//       setOtpVerified(true);
//     } catch (error: any) {
//       toast.error(error.response?.data?.message || "Invalid OTP");
//       setOtpVerified(false);
//     } finally {
//       setOtpVerifying(false);
//     }
//   };

//   const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
//     e.preventDefault();
//     try {
//       setResettingPassword(true);
//       const otp = formData.otp.join("");
//       const res = await axios.post(`${baseUrl}reset-password`, {
//         email: formData.email,
//         otp,
//         password: formData.newPassword,
//         password_confirmation: formData.retypePassword,
//       });
//       SuccessToast(res.data.message, "/login");
//     } catch (error: any) {
//       toast.error(error.response?.data?.message || "Failed to reset password");
//     } finally {
//       setResettingPassword(false);
//     }
//   };

//   return (
//     <div className="page-wrapper">
//       <Container fluid className="p-0">
//         <Row>
//           <Col xs={12}>
//             <div className="login-card login-dark">
//               <div>
//                 <div className="text-center mb-4">
//                   <Link className="logo" to={`/dashboard/default`}>
//                     <Image
//                       className="img-fluid for-light m-auto"
//                       src={dynamicImage(`logo/Logo.png`)}
//                       alt="loginpage"
//                     />
//                     <Image
//                       className="img-fluid for-dark"
//                       src={dynamicImage(`logo/Logo.png`)}
//                       alt="loginpage"
//                     />
//                   </Link>
//                 </div>
//                 <div className="login-main">
//                   <Form className="theme-form" onSubmit={handleSubmit}>
//                     <H2>{ResetYourPassword}</H2>

//                     <FormGroup>
//                       <Label className="pt-0">Enter your email</Label>
//                       <Input
//                         type="email"
//                         id="email"
//                         value={formData.email}
//                         placeholder="Email"
//                         onChange={handleInputChange}
//                         required
//                         disabled={otpSent}
//                       />
//                       <div className="text-end mt-2">
//                         <Btn
//                           color="primary"
//                           block
//                           className="m-t-10"
//                           onClick={sendOtp}
//                           type="button"
//                         >
//                           {loading ? "Sending..." : Send}
//                         </Btn>
//                       </div>
//                     </FormGroup>

//                     {otpSent && (
//                       <div className="mt-4 mb-4">
//                         <span className="reset-password-link">
//                           {"Didn't receive OTP? "}
//                           <a
//                             className="btn-link text-danger"
//                             href={Href}
//                             onClick={(e) => {
//                               e.preventDefault();
//                               sendOtp();
//                             }}
//                           >
//                             {loading ? "Sending..." : Resend}
//                           </a>
//                         </span>
//                       </div>
//                     )}

//                     <FormGroup>
//                       <Label className="pt-0">{EnterOtp}</Label>
//                       <Row>
//                         {[0, 1, 2, 3, 4, 5].map((i) => (
//                           <Col key={i} xs={2}>
//                             <Input
//                               className="text-center opt-text"
//                               type="text"
//                               maxLength={1}
//                               value={formData.otp[i]}
//                               onChange={(e) => handleInputChange(e, i)}
//                               onKeyDown={(e) => handleKeyDown(e, i)}
//                               disabled={!otpSent}
//                               innerRef={(el) => (otpInputRefs.current[i] = el as HTMLInputElement)}
//                             />
//                           </Col>
//                         ))}
//                       </Row>
//                       <div className="text-end mt-2">
//                         <Btn
//                           color="primary"
//                           className="m-t-10 w-100"
//                           onClick={verifyOtp}
//                           type="button"
//                           block
//                           disabled={!otpSent || otpVerifying}
//                         >
//                           {otpVerifying ? "Verifying..." : "Verify OTP"}
//                         </Btn>
//                       </div>
//                     </FormGroup>

//                     <H6 className="mt-4 f-w-700">{CreateYourPassword}</H6>

//                     <FormGroup>
//                       <Label>{NewPassword}</Label>
//                       <div className="form-input position-relative">
//                         <Input
//                           type={isPasswordVisible ? "text" : "password"}
//                           id="newPassword"
//                           value={formData.newPassword}
//                           onChange={handleInputChange}
//                           required
//                           placeholder="*********"
//                           disabled={!otpVerified}
//                         />
//                         <div className="show-hide" onClick={toggle}>
//                           <span
//                             className={!isPasswordVisible ? "show" : ""}
//                           ></span>
//                         </div>
//                       </div>
//                     </FormGroup>

//                     <FormGroup>
//                       <Label>{RetypePassword}</Label>
//                       <div className="form-input position-relative">
//                         <Input
//                           type={isPasswordVisible ? "text" : "password"}
//                           id="retypePassword"
//                           value={formData.retypePassword}
//                           onChange={handleInputChange}
//                           required
//                           placeholder="*********"
//                           disabled={!otpVerified}
//                         />
//                         <div className="show-hide" onClick={toggle}></div>
//                       </div>
//                     </FormGroup>

//                     <Btn
//                       type="submit"
//                       color="primary"
//                       className="w-100 mt-3"
//                       block
//                       disabled={resettingPassword || !otpVerified}
//                     >
//                       {resettingPassword ? "Resetting..." : Done}
//                     </Btn>

//                     <P className="mt-4 mb-0 text-center">
//                       Already have an account?
//                       <Link className="ms-2" to={`/login`}>
//                         {SignIn}
//                       </Link>
//                     </P>
//                   </Form>
//                 </div>
//               </div>
//             </div>
//           </Col>
//         </Row>
//       </Container>
//       <ToastContainer position="top-right" autoClose={3000} />
//     </div>
//   );
// }
